import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  useHistory,
  useLocation,
  withRouter,
} from 'react-router-dom';
import {
  CssBaseline,
  Container,
  Avatar,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { ThemeProvider, makeStyles } from '@material-ui/core/styles';
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { signIn } from '../../redux/actions/authActions';
import darkTheme from '../Theme';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
  },
}));

const LOGIN_MUTATION = gql`
  mutation SignIn($username: String!, $password: String!) {
    signIn(username: $username, password: $password) {
      token
      user {
        id
        username
        name
        email
        role
      }
    }
  }
`;

const SignIn = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { from } = location.state || { from: { pathname: '/' } };

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');

  const [login, { loading }] = useMutation(LOGIN_MUTATION, {
    onCompleted: (data) => {
      const { token } = data.signIn;
      // Store token in localStorage
      localStorage.setItem('authToken', token);
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }
      // Dispatch sign in action
      props.signIn();
      // Redirect to dashboard
      history.replace(from);
    },
    onError: (err) => {
      setError(err.message.replace('GraphQL error: ', '') || 'Authentication failed');
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Username and password are required');
      return;
    }

    try {
      await login({
        variables: {
          username: username.trim(),
          password,
        },
      });
    } catch (err) {
      setError('Authentication failed. Please try again.');
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Cryptower Management Panel
          </Typography>
          <form className={classes.form} noValidate onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" className={classes.errorAlert}>
                {error}
              </Alert>
            )}
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <FormControlLabel
              control={
                <Checkbox
                  value="remember"
                  color="primary"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </form>
        </div>
      </Container>
    </ThemeProvider>
  );
};

SignIn.propTypes = {
  signIn: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => (
  bindActionCreators({ signIn }, dispatch)
);

export default withRouter(connect(null, mapDispatchToProps)(SignIn));
